import React, { useEffect, useState } from 'react';

// Light or dark, chosen by the person and remembered in this browser. Dark is the brand default, so a
// browser with no saved choice stays dark rather than following the operating system.
const KEY = 'theme';
const THEME_COLOUR = { dark: '#020617', light: '#f4f6fb' };
const listeners = new Set();

function read() {
  try { const saved = localStorage.getItem(KEY); return saved === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'light') root.setAttribute('data-theme', 'light'); else root.removeAttribute('data-theme');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOUR[theme] || THEME_COLOUR.dark);
  try { localStorage.setItem(KEY, theme); } catch { /* private mode: the choice lasts for this page only */ }
  for (const listener of listeners) listener(theme);
}

export function initTheme() { applyTheme(read()); }

export function useTheme() {
  const [theme, setTheme] = useState(read);
  useEffect(() => { listeners.add(setTheme); return () => listeners.delete(setTheme); }, []);
  return [theme, (next) => applyTheme(next || (theme === 'light' ? 'dark' : 'light'))];
}

const SUN = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>;
const MOON = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>;

// One button that names the mode it switches to, so it reads as an action rather than a state.
export function ThemeToggle({ className = '' }) {
  const [theme, toggle] = useTheme();
  const light = theme === 'light';
  return <button type="button" className={`theme-toggle ${className}`.trim()} onClick={() => toggle()} aria-pressed={light} title={light ? 'Switch to dark mode' : 'Switch to light mode'}>
    {light ? MOON : SUN}<span>{light ? 'Dark mode' : 'Light mode'}</span>
  </button>;
}
